import React from "react";

/**
 * Aster — the abstract trefoil app mark (three leaves turning around one open
 * aperture). This is the formal app signature (sidebar / app-shell logo), NOT
 * the chat character (that's Bolt).
 *
 * Colors do not follow the light/dark theme. The variants map to the design-
 * system swatches; `onAccent` is the white treatment for a solid brand-green
 * tile (mirrors the 24px FAB).
 *
 * The one exception is an embed whose host declares a brand: `primary` reads
 * the --brand-mark-* variables (see stampBrandMark) so the mark joins the host's
 * product instead of stamping Artie green on it. Unset everywhere else, so the
 * fallbacks below are what the SPA paints.
 */
export type AsterVariant = "primary" | "reversed" | "mono" | "onAccent" | "onSidebar";

// One leaf, rotated 0/120/240° about the 24,24 centre.
const PETAL = "M24 25 C15.5 20 16 10 24 4 C32 10 32.5 20 24 25 Z";

const PALETTES: Record<AsterVariant, { petal: string; ring: string; aperture: string }> = {
  // dark-green trefoil for light backgrounds (host brand wins when stamped)
  primary: {
    petal: "var(--brand-mark-petal, #2d7a4f)",
    ring: "var(--brand-mark-ring, #1a5c38)",
    aperture: "#ffffff",
  },
  // light-green trefoil for the dark app ground
  reversed: { petal: "#4caf7d", ring: "#2d7a4f", aperture: "#152b1e" },
  // single brand-green, aperture knocked to the dark ground
  mono: { petal: "#4caf7d", ring: "#4caf7d", aperture: "#152b1e" },
  // white trefoil on a solid brand-green tile (accent = #2d7a4f)
  onAccent: { petal: "#ffffff", ring: "#ffffff", aperture: "#2d7a4f" },
  // For the mark sitting IN the sidebar. Identical to `primary` until a host
  // brands its sidebar, at which point the mark reverses out of it (an
  // accent-coloured sidebar would otherwise swallow an accent-coloured mark).
  onSidebar: {
    petal: "var(--brand-mark-sidebar-petal, #2d7a4f)",
    ring: "var(--brand-mark-sidebar-ring, #1a5c38)",
    aperture: "var(--brand-mark-sidebar-aperture, #ffffff)",
  },
};

export interface AsterProps {
  size?: number;
  variant?: AsterVariant;
  title?: string;
}

export function Aster({ size = 24, variant = "primary", title = "Aster" }: AsterProps) {
  const c = PALETTES[variant];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      role="img"
      aria-label={title}
    >
      <g fill={c.petal}>
        <path d={PETAL} />
        <path d={PETAL} transform="rotate(120 24 24)" />
        <path d={PETAL} transform="rotate(240 24 24)" />
      </g>
      <circle cx="24" cy="24" r="6" fill={c.ring} />
      <circle cx="24" cy="24" r="3.1" fill={c.aperture} />
    </svg>
  );
}
